/**
 * GraphLegend Component
 * Color legend panel for the ecosystem graph
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Card, Collapse } from 'react-bootstrap';

import { getLegendItems } from '../utils/graphColors';

export default function GraphLegend({ colorBy = 'sector', regionColors }) {
    const [isExpanded, setIsExpanded] = useState(false);
    const legendItems = getLegendItems(colorBy, regionColors);

    // Show first few items, rest collapsed
    const visibleCount = 5;
    const visibleItems = legendItems.slice(0, visibleCount);
    const collapsedItems = legendItems.slice(visibleCount);

    return (
        <Card className="graph-legend bg-white shadow-sm">
            <Card.Body className="p-2">
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <small className="fw-medium text-muted">
                        {colorBy === 'sector' ? 'Sectors' : 'Regions'}
                    </small>
                    {collapsedItems.length > 0 && (
                        <button
                            className="btn btn-link btn-sm p-0 text-decoration-none"
                            onClick={() => setIsExpanded(!isExpanded)}
                            aria-expanded={isExpanded}
                            aria-label={isExpanded ? 'Show less' : 'Show more'}
                        >
                            <small>
                                {isExpanded
                                    ? 'Less'
                                    : `+${collapsedItems.length} more`}
                            </small>
                        </button>
                    )}
                </div>

                <div className="legend-items">
                    {visibleItems.map((item) => (
                        <LegendItem
                            key={item.label}
                            label={item.label}
                            color={item.color}
                        />
                    ))}

                    <Collapse in={isExpanded}>
                        <div>
                            {collapsedItems.map((item) => (
                                <LegendItem
                                    key={item.label}
                                    label={item.label}
                                    color={item.color}
                                />
                            ))}
                        </div>
                    </Collapse>
                </div>
            </Card.Body>
        </Card>
    );
}

function LegendItem({ label, color }) {
    return (
        <div className="d-flex align-items-center mb-1">
            {/* Dynamic backgroundColor from legend data -- must be inline */}
            <span
                className="legend-color-dot me-2"
                style={{ backgroundColor: color }}
            />
            <small className="text-truncate legend-label" title={label}>
                {label}
            </small>
        </div>
    );
}

LegendItem.propTypes = {
    label: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
};

GraphLegend.propTypes = {
    colorBy: PropTypes.oneOf(['sector', 'region']),
    // Tenant-specific region color map (region name -> hex color), built via
    // buildRegionColors(ecosystem.taxonomy.regions). Empty legend in region
    // mode when omitted.
    regionColors: PropTypes.objectOf(PropTypes.string),
};

GraphLegend.defaultProps = {
    colorBy: 'sector',
    regionColors: {},
};
